import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { 
  Scale, 
  Ruler, 
  Flame, 
  Target, 
  TrendingUp,
  Activity,
  Utensils,
  ChevronRight,
  Info
} from "lucide-react";

function MetricCard({ icon: Icon, label, value, unit, color, subtext, progress }) {
  return (
    <div className="p-4 bg-white rounded-xl border border-[#E2E8F0] hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-2">
        <div 
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: `${color}15` }}
        >
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        {progress !== undefined && (
          <span className="text-xs text-[#94A3B8]">{Math.round(progress)}%</span>
        )}
      </div>
      <p className="text-xs text-[#64748B] mb-1">{label}</p>
      <div className="flex items-baseline gap-1">
        <span className="text-2xl font-bold text-[#1A1A2E]">{value}</span>
        {unit && <span className="text-sm text-[#94A3B8]">{unit}</span>}
      </div>
      {subtext && (
        <p className="text-xs mt-1" style={{ color }}>{subtext}</p>
      )}
      {progress !== undefined && (
        <Progress value={progress} className="h-1.5 mt-2" />
      )}
    </div>
  );
}

export default function ProfileMetrics({ profile, onContinue }) {
  if (!profile || !profile.metrics) return null;
  
  const { metrics, formData } = profile;
  const m = metrics;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F0F9FF] via-[#FAFBFC] to-[#F0FDF4] py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-[#10B981] to-[#0EA5E9] mb-4 shadow-lg">
            <TrendingUp className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-[#1A1A2E] mb-2">
            Your Health Profile
          </h1>
          <p className="text-[#64748B]">
            Based on your measurements, here are your personalized metrics
          </p>
        </div>

        {/* Main Metrics Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <MetricCard
            icon={Scale}
            label="BMI"
            value={m.bmi}
            unit="kg/m²"
            color={m.bmiCategory.color}
            subtext={m.bmiCategory.label}
          />
          <MetricCard
            icon={Activity}
            label="Body Fat"
            value={m.bodyFat}
            unit="%"
            color={m.bodyFatCategory.color}
            subtext={m.bodyFatCategory.label}
          />
          <MetricCard
            icon={Flame}
            label="BMR"
            value={m.bmr}
            unit="kcal"
            color="#F59E0B"
            subtext="Base metabolism"
          />
          <MetricCard
            icon={Target}
            label="TDEE"
            value={m.tdee}
            unit="kcal"
            color="#0EA5E9"
            subtext="Daily calories"
          />
        </div>

        {/* Detailed Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Body Composition */}
          <Card className="card-elevated border-0">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <Ruler className="w-5 h-5 text-[#6366F1]" />
                Body Composition
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex justify-between items-center p-3 bg-[#F8FAFC] rounded-lg">
                <span className="text-sm text-[#64748B]">Lean Body Mass</span>
                <span className="font-semibold text-[#1A1A2E]">{m.leanMass} kg</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-[#F8FAFC] rounded-lg">
                <span className="text-sm text-[#64748B]">Ideal Weight</span>
                <span className="font-semibold text-[#1A1A2E]">{m.idealWeight} kg</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-[#F8FAFC] rounded-lg">
                <span className="text-sm text-[#64748B]">Weight Difference</span>
                <span className={`font-semibold ${m.weightDifference > 0 ? 'text-amber-500' : 'text-emerald-500'}`}>
                  {m.weightDifference > 0 ? '+' : ''}{m.weightDifference} kg
                </span>
              </div>
              {m.waistHipRatio != null && (
                <div className="flex justify-between items-center p-3 bg-[#F8FAFC] rounded-lg">
                  <span className="text-sm text-[#64748B]">Waist-to-Hip Ratio</span>
                  <span className="font-semibold text-[#1A1A2E]">{m.waistHipRatio}</span>
                </div>
              )}
              <div className="flex justify-between items-center p-3 bg-[#F8FAFC] rounded-lg">
                <span className="text-sm text-[#64748B]">Waist-to-Height Ratio</span>
                <span className="font-semibold text-[#1A1A2E]">{m.waistHeightRatio}</span>
              </div>
            </CardContent>
          </Card>

          {/* Daily Macros */}
          <Card className="card-elevated border-0">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg flex items-center gap-2">
                <Utensils className="w-5 h-5 text-[#10B981]" />
                Recommended Daily Intake
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="text-center p-4 bg-gradient-to-br from-[#0EA5E9] to-[#6366F1] rounded-xl text-white">
                <p className="text-sm opacity-80">Daily Calories</p>
                <p className="text-3xl font-bold">{m.macros.calories}</p>
                <p className="text-xs opacity-70">kcal/day</p>
              </div>
              
              <div className="grid grid-cols-3 gap-3">
                <div className="text-center p-3 bg-[#FEF3C7] rounded-lg">
                  <p className="text-2xl font-bold text-amber-600">{m.macros.protein}g</p>
                  <p className="text-xs text-amber-700">Protein</p>
                </div>
                <div className="text-center p-3 bg-[#DBEAFE] rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">{m.macros.carbs}g</p>
                  <p className="text-xs text-blue-700">Carbs</p>
                </div>
                <div className="text-center p-3 bg-[#FCE7F3] rounded-lg">
                  <p className="text-2xl font-bold text-pink-600">{m.macros.fat}g</p>
                  <p className="text-xs text-pink-700">Fat</p>
                </div>
              </div>
              
              <div className="flex items-center gap-2 p-3 bg-[#F0FDF4] rounded-lg text-sm text-emerald-700">
                <Info className="w-4 h-4" />
                <span>{m.macros.proteinPerKg}g protein per kg body weight</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Continue Button */}
        <div className="text-center">
          <Button
            onClick={onContinue}
            className="gradient-primary text-white rounded-xl px-8 py-6 text-lg shadow-lg shadow-sky-500/20"
            data-testid="continue-to-dashboard-btn"
          >
            Продължи към Dashboard
            <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </div>

        {/* Disclaimer */}
        <p className="mt-8 text-center text-xs text-[#94A3B8] max-w-lg mx-auto">
          These calculations are estimates based on standard formulas. Individual results may vary. 
          This is not medical advice - consult a healthcare professional for personalized guidance.
        </p>
      </div>
    </div>
  );
}
